import { useEffect } from "react";

import posthog from "posthog-js";

import { createLogger } from "@/utils/logger";

import type { TrackingSource } from "./trackNodeCreation";
import type { TeamKind } from "@/api";
import type { Suggestion } from "@/components/suggestions/types";
import type { SuggestionContext, SuggestionsOpenSource } from "@/models/SuggestionsModel";

type NoProps = Record<string, never>;

export type EventMap = {
  /**
   * Insert block expression.
   * Note: it is not triggered when you paste text combined with math.
   */
  block_expression_added: { doc_id?: string };

  /**
   * Insert inline expression.
   * Note: it is not triggered when you paste text combined with math.
   */
  inline_expression_added: { doc_id?: string };

  /**
   * Event is triggered when any text is typed. It is triggered only once.
   * Node: it is not triggered when you type inside math.
   */
  text_typed: { doc_id?: string };

  /**
   * Document is loaded and rendered.
   */
  document_loaded: { doc_id: string };

  /**
   * Document title is edited.
   */
  document_title_edited: { doc_id?: string };

  /**
   * OCR (Optical Character Recognition) button clicked on an image.
   */
  ocr_clicked: { source: "image_button"; doc_id?: string };

  /**
   * User loaded document without read permissions.
   */
  document_access_denied: { doc_id: string };

  /**
   * Document is loading more than 10 seconds.
   */
  document_loading_slow: { doc_id: string };

  /**
   * Window is focused. It is triggered for any page of the app.
   */
  window_focused: NoProps;

  /**
   * Promo page is viewed.
   */
  about_page_viewed: NoProps;

  /**
   * User clicked on "Get Started", "Login" or "Sign Up" button on the promo page.
   */
  about_get_started_clicked: {
    button: "get_started" | "login" | "signup" | "top_get_started" | "math_editor";
  };

  /**
   * Login page is visited. It includes login via email and via Google.
   */
  login_page_viewed: NoProps;

  /**
   * Signup page is visited. It includes signup via email and via Google.
   */
  signup_page_viewed: NoProps;

  /**
   * "Describe yourself" survey page is submitted with one or more selected options (and optional custom text for "other").
   */
  survey_describe_submitted: {
    options: string[];
  };

  /**
   * "Describe yourself" detail (sub-role) survey page is submitted.
   * The sub-role is tracked here as a separate event property so the existing
   * `surveyRole` (parent) statistics are unaffected.
   */
  survey_describe_detail_submitted: {
    surveySubRole: string;
  };

  /**
   * "Describe yourself" survey page is skipped.
   */
  survey_describe_skipped: NoProps;

  /**
   * "Describe yourself" detail (sub-role) survey page is skipped.
   */
  survey_describe_detail_skipped: NoProps;

  /**
   * Student onboarding: a school is picked from the directory.
   */
  student_school_selected: { school_uid: string };

  /**
   * Student onboarding: the school step is left without picking a school.
   * `reason` tells which exit was used: the Skip button or "I'm not a student".
   * `query` records what was searched before leaving, so missing schools can be added.
   */
  student_school_skipped: { query: string; reason: "skip" | "not_a_student" };

  /**
   * Student onboarding: the "add the school you typed" suggestion is taken.
   * `query` is what was searched, which becomes the school's proposed name.
   */
  student_school_suggested: { query: string };

  /**
   * Student onboarding: a school the directory lacked is submitted for review.
   */
  student_school_submitted: { school_uid: string };

  /**
   * Student onboarding: courses are joined. Sent once the server confirms; `count` is how many were actually joined.
   */
  student_courses_joined: { count: number };

  /**
   * Student onboarding: the course step is skipped.
   */
  student_courses_skipped: NoProps;

  /**
   * "How will you use Corca?" survey option is selected.
   */
  survey_use_submitted: {
    option: string;
  };

  /**
   * Signup survey completed: "use" (usage) submitted.
   * Also set as person properties for PostHog cohorts.
   */
  signup_survey_submitted: {
    surveyUsage: string;
    surveyVersion: number;
  };

  /**
   * Create team step in sign-up flow is submitted.
   */
  create_team_submitted: NoProps;

  /**
   * Create team step in sign-up flow is skipped.
   */
  create_team_skipped: NoProps;

  /**
   * Signup via google. Note: technically login and signup are the same and user can create
   * an account via login page and login via signup page.
   */
  google_auth_signup_clicked: NoProps;

  /**
   * Login via google. Note: technically login and signup are the same and user can create
   * an account via login page and login via signup page.
   */
  google_auth_login_clicked: NoProps;

  /**
   * Login via email. Note: technically login and signup are the same and user can create
   * an account via login path and vice versa.
   */
  email_auth_login_clicked: { email: string };

  /**
   * Login via email. Note: technically login and signup are the same and user can create
   * an account via login path and vice versa.
   */
  email_auth_signup_clicked: { email: string };

  /**
   * Add full name page as a part of sign up flow. We don't show this page for auth via Google because
   * we get user name from Google.
   */
  add_full_name_page_viewed: NoProps;

  /**
   * Add username page as a part of sign up flow.
   */
  add_username_page_viewed: NoProps;

  /**
   * Sign-up flow finished: the user lands on a Getting Started doc.
   * Personal team: `/getting-started` redirects to the personal copy. Created or joined team: the flow ends on `/<permalink>?signup=true`, which lands on the team's copy.
   * `team_kind` tells the paths apart; maps to TikTok's `CompleteRegistration`.
   */
  getting_started_page_viewed: { doc_id: string; team_uid: string; team_kind: TeamKind };

  chat_with_bot_opened: {
    source:
      | "shortcut"
      | "chat_list_button"
      | "floating_input_button"
      | "floating_input_logo"
      | "bottom_menu_button";
  };

  chat_with_team_opened: {
    source: "bottom_menu_button" | "shortcut";
  };

  chat_with_bot_closed: {
    source: "bottom_menu_button" | "shortcut" | "chat_header_button" | "esc_key";
  };

  chat_with_team_closed: {
    source: "bottom_menu_button" | "shortcut" | "chat_header_button" | "esc_key";
  };

  new_chat_with_bot_created: {
    source: "chat_header_button";
  };

  chat_with_bot_list_opened: {
    source: "chat_header_button";
  };

  chat_message_sent: {
    source:
      | "chat_footer_button"
      | "shortcut"
      | "document_floating_input"
      | "document_selection_prompt";
    chat_with_bot: boolean;
  };

  chat_message_insert_button_clicked: NoProps;

  chat_message_delete_button_clicked: NoProps;

  chat_message_resend_button_clicked: NoProps;

  chat_bot_message_copy_clicked: NoProps;

  chat_bot_message_insert_clicked: NoProps;

  /**
   * A notification deep link pointed at a message that is not in the loaded page,
   * so the chat opened without scrolling to it.
   */
  chat_deep_link_message_not_found: {
    doc_id: string;
    chat_uid: string;
    message_uid: string;
    loaded_count: number;
    total_count: number;
  };

  /**
   * New document created in the document tree.
   */
  document_creation_clicked: {
    source: "document" | "folder" | "root_menu" | "inside_panel" | "duplicate" | "top_panel";
  };

  /**
   * New folder created in the document tree.
   */
  folder_creation_clicked: {
    source: "document" | "folder" | "root_menu" | "inside_panel" | "top_panel" | "duplicate";
  };

  import_from_file_clicked: { source: "top_panel" };

  download_latex_clicked: { doc_id: string };

  download_pdf_clicked: { doc_id: string };

  export_to_overleaf_clicked: { doc_id: string };

  import_from_overleaf_clicked: NoProps;

  import_from_notion_connect_clicked: NoProps;

  import_from_notion_import_clicked: { page_count: number };

  import_from_notion_refresh_pages_clicked: NoProps;

  import_from_notion_switch_accounts_clicked: NoProps;

  import_from_notion_disconnect_clicked: NoProps;

  fork_document_clicked: {
    doc_id: string;
    source: "document" | "bottom_panel" | "mobile_download_panel";
  };

  copy_link_clicked: { doc_id: string; source: "document" };

  fork_folder_clicked: { folder_id: string; source: "folder" | "duplicate" };

  /**
   * Download menu button clicked to open dropdown
   */
  download_menu_opened: { doc_id: string };

  graph_insert: {
    source: "graph_button" | "function" | "expression";
  };

  graph_add_expression: { source: "button" };

  graph_remove_expression: { source: "button" };

  graph_grid_type_changed: { type: number };

  graph_grid_color_changed: NoProps;

  graph_alignment_changed: {
    alignment: "positive_x" | "positive_y" | "positive_x_y";
  };

  graph_axes_visibility_toggled: { visible: boolean };

  graph_axes_color_changed: NoProps;

  graph_bg_color_changed: NoProps;

  graph_axis_labels_toggled: { enabled: boolean };

  /**
   * Variable created from suggestions, continuous typing, multinode, LaTeX import, OCR, or expression
   */
  variable_created: {
    source: TrackingSource;
    name?: string;
    doc_id?: string;
  };

  /**
   * Constant created from suggestions, continuous typing, multinode, LaTeX import, OCR, or expression
   */
  constant_created: {
    source: TrackingSource;
    name?: string;
    doc_id?: string;
  };

  /**
   * Function created from suggestions, continuous typing, multinode, LaTeX import, OCR, or expression
   */
  function_created: {
    source: TrackingSource;
    name?: string;
    doc_id?: string;
  };

  /**
   * Operator symbol created from suggestions, continuous typing, multinode, LaTeX import, OCR, or expression
   */
  operator_symbol_created: {
    source: TrackingSource;
    name?: string;
    doc_id?: string;
  };

  /**
   * Group created from suggestions, continuous typing, multinode, LaTeX import, OCR, or expression
   */
  group_created: {
    source: TrackingSource;
    name?: string;
    doc_id?: string;
  };

  /**
   * Number created from suggestions, continuous typing, multinode, LaTeX import, OCR, or expression
   */
  number_created: {
    source: TrackingSource;
    name?: string;
    doc_id?: string;
  };

  /**
   * Expression created from suggestions or continuous typing
   */
  expression_created: {
    source: Extract<TrackingSource, "suggest" | "continuous typing">;
    name?: string;
    doc_id?: string;
  };

  /**
   * Share button clicked to open share dialog
   */
  share_button_clicked: {
    doc_id: string;
  };

  /**
   * Share menu closed
   */
  share_menu_closed: {
    doc_id: string;
  };

  /**
   * User invited someone to share document
   */
  share_user_invited: {
    username: string;
    doc_id: string;
  };

  /**
   * Copy link button clicked in share dialog
   */
  share_copy_link: {
    doc_id: string;
  };

  /**
   * QR Code button clicked to view QR code
   */
  share_qr_code_clicked: {
    doc_id: string;
  };

  /**
   * QR Code downloaded as PNG
   */
  share_qr_code_downloaded: {
    doc_id: string;
  };

  /**
   * Permission group changed (Only Invited People / Anyone with Link)
   */
  share_permission_group_changed: {
    setting: string;
    doc_id: string;
  };

  /**
   * Permission setting changed (Can Edit / Can View / etc)
   */
  share_permission_setting_changed: {
    setting: string;
    doc_id: string;
  };

  /**
   * Individual user permission changed or removed
   */
  share_user_permission_setting_changed: {
    setting: string;
    username: string;
    doc_id: string;
  };

  /**
   * Hints suggestion shown to the user
   */
  hints_suggestion_show: {
    hint_type: string;
    expression: string;
    doc_id: string;
  };

  /**
   * Hints suggestion accepted by the user (Tab key)
   */
  hints_suggestion_accepted: {
    hint_type: string;
    doc_id: string;
  };

  /**
   * Hints suggestion cycled (Shift+Tab key)
   */
  hints_suggestion_cycled: {
    from_hint_type?: string;
    to_hint_type?: string;
    doc_id: string;
  };

  /**
   * Suggestions popup opened deliberately — by right-click, or by Mod+Space.
   * Not fired when it opens from typing.
   */
  suggestions_opened: {
    context: SuggestionContext;
    source: SuggestionsOpenSource;
    doc_id?: string;
  };

  /**
   * A suggestion carrying a `trackingId` was picked. Only opted-in suggestions
   * fire this, so typeahead auto-picks never appear here.
   */
  suggestions_action_selected: {
    action: string;
    suggestion_type: Suggestion["type"];
    context?: SuggestionContext;
    doc_id?: string;
  };

  /**
   * An "Ask AI" prompt was sent. `ai_suggestion` means the user picked one of the
   * generated questions, `typed` means they wrote their own. `entry` is where the
   * prompt was opened: the suggestions popup (right-click or shortcut) or the line menu.
   */
  selection_prompt_submitted: {
    source: "ai_suggestion" | "typed";
    entry: "suggestions" | "line_menu";
    doc_id?: string;
  };

  /**
   * Right panel clicked (any panel in the right sidebar)
   */
  right_panel_clicked: {
    source:
      | "create_blocks_panel"
      | "graph_preview_panel"
      | "variable_style"
      | "function_style"
      | "constant_style"
      | "number_style"
      | "group_style"
      | "placeholder_style"
      | "text_style"
      | "table_cell_style"
      | "table_style"
      | "code_block_style"
      | "image_style"
      | "graph_style"
      | "math_style"
      | "symbol_palette"
      | "node_props";
  };

  /**
   * Fired when nodes are created from OCR (once per OCR action, regardless of node count)
   */
  nodes_created_from_ocr: { doc_id?: string };

  /**
   * Fired when nodes are created from multinode suggestion (once per multinode action, regardless of node count)
   */
  nodes_created_from_multinode: { doc_id?: string };

  /**
   * Fired when nodes are created from LaTeX import/paste (once per import action, regardless of node count)
   */
  nodes_created_from_latex_import: { doc_id?: string };

  embed_insert: {
    domain: string;
    is_rich_media?: boolean;
    full_url: string;
    error_message?: string;
  };

  /**
   * Notifications panel opened from the bell button in the sidebar.
   */
  notifications_panel_opened: { unread_count: number };

  /**
   * Notification row clicked to navigate to its target. Also fires when navigation
   * is handled in place (e.g. a message notification opening a chat in the current doc).
   */
  notification_clicked: {
    type: "invite" | "document_permission" | "message" | "mention" | "membership";
    is_read: boolean;
  };

  /**
   * Action button clicked inside a notification.
   */
  notification_action_clicked: {
    type: "invite" | "document_permission";
    action: "accept" | "decline" | "open_document";
  };
};

export type EventKey = keyof EventMap;

interface DataLayerEvent {
  event: string;
  [key: string]: string | number;
}

declare global {
  interface Window {
    dataLayer?: DataLayerEvent[];
    gtag?: (type: string, name: string, props: Record<string, string | number>) => void;
    ttq?: {
      track: (event: string, properties?: Record<string, unknown>) => void;
      page: () => void;
    };
  }
}

export function trackEvent<T extends EventKey>(
  event: T,
  properties: EventMap[T] = {} as EventMap[T],
) {
  const logger = createLogger("trackEvent");
  logger.debug(event, properties);

  if (posthog) {
    posthog.capture(event, properties);
  }

  if (typeof window !== "undefined" && typeof window.gtag === "function") {
    // Convert boolean values to strings for gtag
    const gtagProperties = Object.fromEntries(
      Object.entries(properties).map(([key, value]) => [
        key,
        typeof value === "boolean" ? String(value) : value,
      ]),
    );
    window.gtag("event", event, gtagProperties);
  }

  // Track with TikTok Pixel
  if (typeof window !== "undefined" && typeof window.ttq !== "undefined") {
    // Fires once the sign-up flow lands on a Getting Started doc, so it is TikTok's standard conversion event
    if (event === "getting_started_page_viewed") {
      window.ttq.track("CompleteRegistration");
    }

    // Also track the raw event name for custom reporting in TikTok Events Manager
    window.ttq.track(event, properties);
  }
}

/**
 * Sets properties on the PostHog person (user) for cohorts and segmentation.
 */
export function setPostHogPersonProperties(properties: Record<string, unknown>): void {
  if (posthog) {
    posthog.setPersonProperties(properties);
  }
}

/**
 * React hook to track event once the component is rendered and avoid duplicate
 * events on component re-render.
 */
export function useTrackEventOnce<T extends EventKey>(event: T, properties: EventMap[T]) {
  useEffect(() => {
    trackEvent(event, properties);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
}

/**
 * In-memory store for tracking events that should only fire once per session.
 * Set of event names that have already been tracked this session.
 */
const trackedEventsThisSession = new Set<string>();

/**
 * Tracks an analytics event only once per browser session.
 *
 * @param event - Event name / key.
 * @param properties - Event-specific payload.
 * @param key - Optional deduplication key used to scope the "once per session" behavior.
 *   When provided, the event will be tracked once per session **per key value**.
 *   Use this to distinguish the same event across different entities or contexts
 *   (for example, per document, per user, or per editor instance).
 *
 *   Example:
 *   trackEventOncePerSession(
 *     "text_typed",
 *     { doc_id: doc.id },
 *     doc.id,
 *   );
 */
export function trackEventOncePerSession<T extends EventKey>(
  event: T,
  properties: EventMap[T],
  key: string = "",
) {
  if (!trackedEventsThisSession.has(`${event}${key}`)) {
    trackedEventsThisSession.add(`${event}${key}`);
    trackEvent(event, properties);
  }
}
